// Shimmering Prism — background post-process fragment shader (RawShaderMaterial, GLSL ES 1.00).
//
// A single pass that refines ONLY the background render target (tDiffuse). It is composited to the
// screen below the HTML UI, so it never touches text / cards / nav / images. The logical order is
// preserved deliberately (see the task spec):
//
//   1. Level adjustment / black lift  — applied to EACH RGB sample, before recombination, so the
//      lift is part of the per-channel colour, not slapped on the final mix.
//   2. Chromatic aberration           — radial RGB separation from screen centre, magnitude in
//      pixels (red shifted out, blue shifted in, green centred), UVs clamped to avoid edge bleed.
//   3. Film noise                     — subtle monochrome animated grain added at the very end.
//
// highp is used (desktop-only background) for stable grain math + sub-pixel offsets.
export default `
precision highp float;

uniform sampler2D tDiffuse;     // background render target
uniform vec2  uResolution;      // drawing-buffer size in pixels (tracks adaptive resolution)
uniform float uNoiseTime;       // real-seconds accumulator (frozen under reduced motion)
uniform float uBlackLift;       // raises black without washing out white: c*(1-lift)+lift
uniform float uAberrationPx;    // chromatic aberration, in pixels at the screen edge
uniform float uNoiseAmount;     // film-grain peak amplitude (0 = off)
uniform float uNoiseSpeed;      // grain animation time-scale (legacy reference used 10.0)

varying vec2 vUv;

// Black lift: c * (1 - lift) + lift. Pure black -> lift; white stays ~white (only scaled by 1-lift).
vec3 levelAdjust(vec3 c) {
    return c * (1.0 - uBlackLift) + uBlackLift;
}

void main() {
    // 2a. Radial chromatic-aberration offset (in UV space), zero at centre, growing toward edges.
    vec2 fromCenter = vUv - 0.5;
    float dist = length(fromCenter);
    vec2 dir = dist > 0.0 ? fromCenter / dist : vec2(0.0);
    vec2 texel = 1.0 / uResolution;                       // one device pixel, in UV
    vec2 offset = dir * (uAberrationPx * dist * 2.0) * texel;

    vec2 uvR = clamp(vUv + offset, 0.0, 1.0);
    vec2 uvG = vUv;
    vec2 uvB = clamp(vUv - offset, 0.0, 1.0);

    // 1 + 2b. Level-adjust each sample BEFORE recombining the separated channels.
    vec3 cR = levelAdjust(texture2D(tDiffuse, uvR).rgb);
    vec3 cG = levelAdjust(texture2D(tDiffuse, uvG).rgb);
    vec3 cB = levelAdjust(texture2D(tDiffuse, uvB).rgb);
    vec3 color = vec3(cR.r, cG.g, cB.b);

    // 3. Film grain — ported from the legacy background's blur pass (the look the site used before).
    //    The reference's base pattern lands in [-0.005, 0.005]; we normalise it (/0.005) to [-1, 1] so
    //    the existing 0–0.1 "noise intensity" uniform (uNoiseAmount) stays the peak grain amplitude.
    //    uNoiseTime is frozen under reduced motion (static grain); uNoiseSpeed is the time scale.
    float strength = uNoiseAmount;
    float x = (vUv.x + 4.0) * (vUv.y + 4.0) * (uNoiseTime * uNoiseSpeed);
    vec3 grain = vec3((mod((mod(x, 13.0) + 1.0) * (mod(x, 123.0) + 1.0), 0.01) - 0.005) / 0.005) * strength;
    color += grain;

    gl_FragColor = vec4(color, 1.0);
}
`;
